import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, TextInput, Select, Modal,
} from "@/components/common/UI";
import { VarianceBadge } from "@/components/zbc/VarianceBadge";
import { useAuth, hasRole } from "@/lib/auth";
import {
    Calculator, Plus, Search, AlertTriangle, RefreshCw, Clock,
    CheckCircle2, FileText, Copy, Trash2, Sparkles, ChevronDown,
    TrendingUp, TrendingDown, Wallet,
} from "lucide-react";
import { fmtINR, fmtDate } from "@/lib/format";
import { toast } from "sonner";

export default function ZBCList() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [plants, setPlants] = useState([]);
    const [error, setError] = useState("");
    const [pendingCount, setPendingCount] = useState(0);

    // Filters
    const [search, setSearch] = useState("");
    const [filterCustomer, setFilterCustomer] = useState("ALL");
    const [filterPlant, setFilterPlant] = useState("ALL");
    const [filterStatus, setFilterStatus] = useState("ALL");
    const [filterBadge, setFilterBadge] = useState("ALL");
    const [filterRate, setFilterRate] = useState("ALL");

    // Recalc modal
    const [recalcZbc, setRecalcZbc] = useState(null);
    const [recalcPreview, setRecalcPreview] = useState(null);
    const [recalcSubmitting, setRecalcSubmitting] = useState(false);

    // New-ZBC mode picker menu
    const [newMenuOpen, setNewMenuOpen] = useState(false);

    const canEdit = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");
    const canDelete = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");

    const load = () =>
        Promise.all([
            api.get("/zbcs"),
            api.get("/customers"),
            api.get("/plants"),
            api.get("/zbcs/pending-recalc-count"),
        ])
            .then(([z, c, p, pc]) => {
                setRows(z.data);
                setCustomers(c.data);
                setPlants(p.data);
                setPendingCount(pc.data.count);
            })
            .catch((e) => setError(formatApiError(e)));

    useEffect(() => { load(); }, []);

    const customerMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
    const plantMap = useMemo(() => Object.fromEntries(plants.map((p) => [p.id, p])), [plants]);

    const filtered = useMemo(() => {
        if (!rows) return null;
        return rows.filter((z) => {
            if (search && !(z.part_number?.toLowerCase().includes(search.toLowerCase()) ||
                            z.part_name?.toLowerCase().includes(search.toLowerCase()) ||
                            z.navision_code?.toLowerCase().includes(search.toLowerCase()))) return false;
            if (filterCustomer !== "ALL" && z.customer_id !== filterCustomer) return false;
            if (filterPlant !== "ALL" && z.plant_id !== filterPlant) return false;
            if (filterStatus !== "ALL" && z.status !== filterStatus) return false;
            if (filterBadge !== "ALL" && z.variance_badge !== filterBadge) return false;
            if (filterRate === "PENDING" && !z.recalc_pending) return false;
            if (filterRate === "UP_TO_DATE" && z.recalc_pending) return false;
            return true;
        });
    }, [rows, search, filterCustomer, filterPlant, filterStatus, filterBadge, filterRate]);

    const isStaleQuote = (z) => {
        if (!z.quote_date) return false;
        const days = (Date.now() - new Date(z.quote_date).getTime()) / (1000 * 60 * 60 * 24);
        return days > 180;
    };

    const openRecalc = async (z) => {
        try {
            const { data } = await api.post(`/zbcs/${z.id}/recalculate-preview`);
            setRecalcZbc(z);
            setRecalcPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const confirmRecalc = async () => {
        if (!recalcZbc) return;
        setRecalcSubmitting(true);
        try {
            await api.post(`/zbcs/${recalcZbc.id}/recalculate`);
            toast.success("ZBC recalculated", { description: `${recalcZbc.part_number} updated to new should-cost` });
            setRecalcZbc(null); setRecalcPreview(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecalcSubmitting(false); }
    };

    const keepOldRate = async () => {
        if (!recalcZbc) return;
        try {
            await api.post(`/zbcs/${recalcZbc.id}/keep-old-rate`);
            toast.info("Kept old rate", { description: "Flag remains until you recalculate later." });
            setRecalcZbc(null); setRecalcPreview(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const duplicate = async (z) => {
        try {
            const { data } = await api.post(`/zbcs/${z.id}/duplicate`);
            toast.success("Duplicated");
            navigate(`/zbc/${data.id}/edit`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeZbc = async (z) => {
        if (!window.confirm(`Delete ZBC ${z.part_number}? This action cannot be undone.`)) return;
        try {
            await api.delete(`/zbcs/${z.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ---- Multi-select + Combine ----
    const [selected, setSelected] = useState(new Set());
    const [combineOpen, setCombineOpen] = useState(false);
    const [combineName, setCombineName] = useState("");
    const [combinePartNo, setCombinePartNo] = useState("");
    const [combineSubmitting, setCombineSubmitting] = useState(false);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        setSelected((prev) => {
            if (!filtered) return prev;
            const allIds = filtered.map((r) => r.id);
            if (prev.size >= allIds.length) return new Set();
            return new Set(allIds);
        });
    };
    const submitCombine = async () => {
        if (selected.size < 2) return toast.error("Select at least two ZBCs to combine");
        if (!combinePartNo.trim()) return toast.error("Enter a part number for the combined ZBC");
        setCombineSubmitting(true);
        try {
            const { data } = await api.post("/zbcs/combine", {
                zbc_ids: Array.from(selected),
                part_number: combinePartNo.trim(),
                part_name: combineName.trim() || `Combined (${selected.size} parts)`,
            });
            toast.success("Combined ZBC created", { description: `Should-cost ${data.should_cost.toLocaleString()}` });
            setCombineOpen(false);
            setSelected(new Set());
            setCombineName("");
            setCombinePartNo("");
            navigate(`/zbc/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCombineSubmitting(false); }
    };

    // ---- Financial Impact ----
    const [impactOpen, setImpactOpen] = useState(false);
    const [impactData, setImpactData] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);
    const [impactVolumes, setImpactVolumes] = useState({}); // { zbcId: annualVolume }

    const fetchImpact = async (volumes) => {
        setImpactLoading(true);
        try {
            const { data } = await api.post("/zbcs/impact", {
                zbc_ids: Array.from(selected),
                annual_volumes: volumes || undefined,
            });
            setImpactData(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setImpactLoading(false);
        }
    };

    const openImpact = async () => {
        if (selected.size === 0) return toast.error("Select at least one ZBC");
        setImpactOpen(true);
        setImpactVolumes({});
        await fetchImpact(null);
    };

    const updateImpactVolume = (zbcId, value) => {
        const num = parseFloat(value);
        const next = { ...impactVolumes, [zbcId]: isNaN(num) ? 0 : num };
        setImpactVolumes(next);
        // Debounced-ish: user changes are cheap on backend
        fetchImpact(next);
    };

    return (
        <div data-testid="zbc-list-page">
            <PageHeader
                eyebrow="MODULE · STEP 2 OF 4"
                title="ZBC Costings"
                subtitle="Should-cost analysis for every part. Each ZBC compares ABC COMPANY's calculated manufacturing cost against the supplier's quote."
                actions={
                    <div className="flex items-center gap-2">
                        <SlateButton onClick={() => navigate("/zbc/variance-report")} variant="outline">
                            <FileText className="h-4 w-4" /> Variance Report
                        </SlateButton>
                        {canEdit && (
                            <div className="relative" data-testid="new-zbc-menu">
                                <div className="inline-flex rounded-md shadow-sm">
                                    <CopperButton onClick={() => navigate("/zbc/new")} data-testid="zbc-new-btn" className="rounded-r-none">
                                        <Plus className="h-4 w-4" /> New ZBC
                                    </CopperButton>
                                    <button
                                        type="button"
                                        onClick={() => setNewMenuOpen(v => !v)}
                                        className="h-9 px-2 rounded-l-none rounded-md border border-l-0 border-[hsl(var(--copper))] bg-[hsl(var(--copper))] text-white hover:bg-[hsl(var(--copper))]/90 -ml-px"
                                        title="Choose mode"
                                        data-testid="new-zbc-menu-toggle"
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </button>
                                </div>
                                {newMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setNewMenuOpen(false)} />
                                        <div className="absolute right-0 mt-1.5 w-64 bg-white rounded-md shadow-lg border border-slate-200 z-50 py-1">
                                            <button
                                                onClick={() => { setNewMenuOpen(false); navigate("/zbc/new"); }}
                                                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5"
                                                data-testid="new-zbc-manual"
                                            >
                                                <Calculator className="h-4 w-4 text-slate-600 mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="text-[13px] font-semibold text-slate-800">Manual ZBC</div>
                                                    <div className="text-[11px] text-slate-500">Full form entry: you input every section.</div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => { setNewMenuOpen(false); navigate("/zbc/ai-new"); }}
                                                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5 border-t border-slate-100"
                                                data-testid="new-zbc-ai"
                                            >
                                                <Sparkles className="h-4 w-4 text-[hsl(var(--copper))] mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="text-[13px] font-semibold text-slate-800">Intelligent-Generated ZBC</div>
                                                    <div className="text-[11px] text-slate-500">Upload drawing PDF, CostIQ extracts geometry & builds route.</div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => { setNewMenuOpen(false); navigate("/zbc/supplier-compare"); }}
                                                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5 border-t border-slate-100"
                                                data-testid="new-zbc-supplier"
                                            >
                                                <FileText className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="text-[13px] font-semibold text-slate-800">Supplier Workbook (Compare)</div>
                                                    <div className="text-[11px] text-slate-500">Upload vendor&#39;s .xlsx, auto-compare against ABC COMPANY ZBCs.</div>
                                                </div>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                }
            />

            {/* Pending recalc banner */}
            {pendingCount > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 mb-4 flex items-start gap-3 animate-fade-up">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-amber-900">
                            {pendingCount} ZBC{pendingCount > 1 ? "s have" : " has"} a pending steel rate update and need{pendingCount === 1 ? "s" : ""} recalculation.
                        </div>
                        <div className="text-xs text-amber-800 mt-1">
                            The should-cost on these ZBCs is based on an old rate and may be incorrect.
                        </div>
                    </div>
                    <button
                        onClick={() => { setFilterRate("PENDING"); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                        className="text-xs font-medium text-amber-900 underline hover:text-amber-700 shrink-0 self-center"
                    >
                        View flagged ZBCs →
                    </button>
                </div>
            )}

            {/* Filter bar */}
            <Card className="p-3 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    <div className="relative col-span-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <TextInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by part number, name, navision code…"
                            className="pl-8 h-8 text-[12px]"
                        />
                    </div>
                    <Select value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)} className="h-8 text-[12px]">
                        <option value="ALL">All customers</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </Select>
                    <Select value={filterPlant} onChange={(e) => setFilterPlant(e.target.value)} className="h-8 text-[12px]">
                        <option value="ALL">All plants</option>
                        {plants.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
                    </Select>
                    <Select value={filterBadge} onChange={(e) => setFilterBadge(e.target.value)} className="h-8 text-[12px]">
                        <option value="ALL">All variance</option>
                        <option value="GREEN">Green &lt; 5%</option>
                        <option value="AMBER">Amber 5–15%</option>
                        <option value="RED">Red &gt; 15%</option>
                        <option value="FLAG">Flag (below cost)</option>
                    </Select>
                    <Select value={filterRate} onChange={(e) => setFilterRate(e.target.value)} className="h-8 text-[12px]">
                        <option value="ALL">All rate states</option>
                        <option value="PENDING">Recalc pending</option>
                        <option value="UP_TO_DATE">Up to date</option>
                    </Select>
                </div>
            </Card>

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}
            {filtered && filtered.length === 0 && (
                <EmptyState
                    icon={Calculator}
                    title="No ZBCs match your filters"
                    description={rows.length === 0 ? "Create your first should-cost analysis." : "Try clearing some filters."}
                    action={rows.length === 0 && canEdit && (
                        <CopperButton onClick={() => navigate("/zbc/new")}>
                            <Plus className="h-4 w-4" /> Create First ZBC
                        </CopperButton>
                    )}
                />
            )}

            {filtered && filtered.length > 0 && (
                <Card>
                    {selected.size > 0 && (
                        <div className="px-4 py-3 border-b border-slate-100 bg-amber-50/50 flex items-center justify-between" data-testid="combine-bar">
                            <div className="flex items-center gap-4">
                                <span className="text-[13px] font-semibold text-slate-800">
                                    {selected.size} ZBC(s) selected
                                </span>
                                <button
                                    onClick={() => setSelected(new Set())}
                                    className="text-[11px] text-slate-500 hover:text-slate-800"
                                >Clear</button>
                            </div>
                            <div className="flex gap-2">
                                <SlateButton
                                    onClick={openImpact}
                                    data-testid="impact-btn"
                                    className="h-8 text-[12px]"
                                    variant="outline"
                                >
                                    <Wallet className="h-3.5 w-3.5" /> Financial Impact ({selected.size})
                                </SlateButton>
                                {selected.size >= 2 && canEdit && (
                                    <CopperButton
                                        onClick={() => setCombineOpen(true)}
                                        data-testid="combine-btn"
                                        className="h-8 text-[12px]"
                                    >
                                        <Copy className="h-3.5 w-3.5" /> Create Combined ZBC ({selected.size})
                                    </CopperButton>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full table-rowhover">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60">
                                    <th className="py-2 pl-3 pr-2 w-8">
                                        {canEdit && (
                                            <input
                                                type="checkbox"
                                                data-testid="zbc-select-all"
                                                checked={!!filtered && filtered.length > 0 && selected.size === filtered.length}
                                                onChange={toggleSelectAll}
                                                className="h-3.5 w-3.5 accent-[hsl(var(--copper))]"
                                            />
                                        )}
                                    </th>
                                    <th className="eyebrow text-left py-2 px-3">Part Number</th>
                                    <th className="eyebrow text-left py-2 px-3">Part Name</th>
                                    <th className="eyebrow text-left py-2 px-3">Customer</th>
                                    <th className="eyebrow text-left py-2 px-3">Plant</th>
                                    <th className="eyebrow text-right py-2 px-3">Should-Cost</th>
                                    <th className="eyebrow text-right py-2 px-3">Supplier ₹</th>
                                    <th className="eyebrow text-left py-2 px-3">Variance</th>
                                    <th className="eyebrow text-left py-2 px-3">Status</th>
                                    <th className="eyebrow text-center py-2 px-2">Flags</th>
                                    <th className="eyebrow text-left py-2 px-3">Created</th>
                                    <th className="eyebrow text-right py-2 px-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((z) => {
                                    const c = customerMap[z.customer_id];
                                    const p = plantMap[z.plant_id];
                                    const stale = isStaleQuote(z);
                                    return (
                                        <tr key={z.id} className="border-b border-slate-100">
                                            <td className="py-1.5 pl-3 pr-2">
                                                {canEdit && (
                                                    <input
                                                        type="checkbox"
                                                        data-testid={`zbc-select-${z.id}`}
                                                        checked={selected.has(z.id)}
                                                        onChange={() => toggleSelect(z.id)}
                                                        className="h-3.5 w-3.5 accent-[hsl(var(--copper))]"
                                                    />
                                                )}
                                            </td>
                                            <td className="py-1.5 px-3 text-[13px] font-mono font-semibold">
                                                <Link to={`/zbc/${z.id}`} className="hover:text-[hsl(var(--copper))]">{z.part_number}</Link>
                                            </td>
                                            <td className="py-1.5 px-3 text-[13px] max-w-[180px] truncate">{z.part_name}</td>
                                            <td className="py-1.5 px-3 text-[12px] text-slate-600">{c?.code || "—"}</td>
                                            <td className="py-1.5 px-3 text-[12px] font-mono text-slate-600">{p?.code || "—"}</td>
                                            <td className={`py-1.5 px-3 text-right font-mono tabular-nums text-[13px] font-semibold ${z.recalc_pending ? "text-amber-600" : "text-slate-900"}`}>
                                                {fmtINR(z.should_cost || 0)}
                                            </td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{z.supplier_quote ? fmtINR(z.supplier_quote) : "—"}</td>
                                            <td className="py-1.5 px-3"><VarianceBadge badge={z.variance_badge} pct={z.variance_pct} /></td>
                                            <td className="py-1.5 px-3">
                                                <Pill tone={z.status === "VERIFIED" ? "blue" : "slate"}>
                                                    {z.status === "VERIFIED" ? <><CheckCircle2 className="h-2.5 w-2.5" /> Verified</> : "Draft"}
                                                </Pill>
                                            </td>
                                            <td className="py-1.5 px-2 text-center">
                                                <div className="inline-flex items-center gap-1">
                                                    {z.recalc_pending && (
                                                        <span title="Rate update, recalc pending" className="inline-flex items-center justify-center h-5 w-5 rounded bg-amber-100">
                                                            <RefreshCw className="h-3 w-3 text-amber-700" />
                                                        </span>
                                                    )}
                                                    {stale && (
                                                        <span title="Quote > 180 days" className="inline-flex items-center justify-center h-5 w-5 rounded bg-red-100">
                                                            <Clock className="h-3 w-3 text-red-700" />
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-1.5 px-3 text-[11px] font-mono tabular-nums text-slate-500">{fmtDate(z.created_at)}</td>
                                            <td className="py-1.5 px-3 text-right">
                                                <div className="inline-flex gap-1">
                                                    {z.recalc_pending && canEdit && (
                                                        <SlateButton onClick={() => openRecalc(z)} className="h-7 px-2 text-[11px]">
                                                            <RefreshCw className="h-3 w-3" /> Recalc
                                                        </SlateButton>
                                                    )}
                                                    <SlateButton onClick={() => navigate(`/zbc/${z.id}`)} variant="ghost" className="h-7 px-2 text-[11px]">View</SlateButton>
                                                    {canEdit && (
                                                        <SlateButton onClick={() => navigate(`/zbc/${z.id}/edit`)} variant="ghost" className="h-7 px-2 text-[11px]">Edit</SlateButton>
                                                    )}
                                                    {canEdit && (
                                                        <SlateButton onClick={() => duplicate(z)} variant="ghost" className="h-7 px-2 text-[11px]" title="Duplicate this ZBC">
                                                            <Copy className="h-3 w-3" /> Duplicate
                                                        </SlateButton>
                                                    )}
                                                    {canDelete && (
                                                        <SlateButton
                                                            onClick={() => removeZbc(z)}
                                                            variant="ghost"
                                                            className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50 hover:text-red-700"
                                                            title="Delete this ZBC"
                                                            data-testid={`zbc-delete-${z.id}`}
                                                        >
                                                            <Trash2 className="h-3 w-3" /> Delete
                                                        </SlateButton>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Recalc modal */}
            <Modal
                open={!!recalcZbc}
                onClose={() => { setRecalcZbc(null); setRecalcPreview(null); }}
                title={recalcZbc ? `Recalculate ZBC · ${recalcZbc.part_number} ${recalcZbc.part_name}` : ""}
                size="lg"
                footer={
                    <>
                        <SlateButton variant="outline" onClick={keepOldRate}>Keep Old Rate</SlateButton>
                        <CopperButton onClick={confirmRecalc} disabled={recalcSubmitting}>
                            {recalcSubmitting ? "Recalculating…" : "Confirm Recalculation"}
                        </CopperButton>
                    </>
                }
            >
                {recalcZbc && recalcPreview && (
                    <div className="space-y-4">
                        <div className="text-[13px] text-slate-600">
                            {recalcZbc.recalc_reason || "Steel rate has been updated by Corporate Admin."}
                        </div>

                        <div className="rounded-md border border-slate-200 overflow-hidden">
                            <div className="grid grid-cols-2 divide-x divide-slate-200">
                                <div className="px-4 py-3 bg-slate-50">
                                    <div className="eyebrow text-slate-500">OLD RATE</div>
                                    <div className="font-mono tabular-nums text-lg font-semibold">{fmtINR(recalcPreview.old_rate)}<span className="text-xs text-slate-500">/kg</span></div>
                                </div>
                                <div className="px-4 py-3 bg-[hsl(var(--copper-soft))]">
                                    <div className="eyebrow text-slate-600">NEW RATE</div>
                                    <div className="font-mono tabular-nums text-lg font-semibold text-[hsl(var(--copper))]">{fmtINR(recalcPreview.new_rate)}<span className="text-xs">/kg</span></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 divide-x divide-slate-200 border-t border-slate-200">
                                <div className="px-4 py-3">
                                    <div className="eyebrow text-slate-500">CURRENT SHOULD-COST</div>
                                    <div className="font-mono tabular-nums text-base font-semibold">{fmtINR(recalcPreview.old_should_cost)}</div>
                                </div>
                                <div className="px-4 py-3">
                                    <div className="eyebrow text-slate-500">NEW SHOULD-COST</div>
                                    <div className="font-mono tabular-nums text-base font-semibold text-[hsl(var(--copper))]">{fmtINR(recalcPreview.new_should_cost)}</div>
                                </div>
                                <div className="px-4 py-3">
                                    <div className="eyebrow text-slate-500">IMPACT</div>
                                    <div className={`font-mono tabular-nums text-base font-semibold ${recalcPreview.impact >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                        {recalcPreview.impact >= 0 ? "+" : ""}{fmtINR(recalcPreview.impact)} <span className="text-xs">({recalcPreview.impact_pct >= 0 ? "+" : ""}{recalcPreview.impact_pct}%)</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                            <div>
                                If this ZBC is linked to an approved RFQ, the RFQ price will <span className="font-semibold">not</span> change automatically.
                                You will need to review that RFQ separately.
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Combine ZBCs modal */}
            <div data-testid="combine-modal" data-open={combineOpen ? "true" : "false"}>
            <Modal
                open={combineOpen}
                onClose={() => setCombineOpen(false)}
                title="Create Combined ZBC"
            >
                <div className="space-y-3 text-[13px]">
                    <div className="text-slate-600">
                        Sums material + conversion + cutting + welding + allowances for the {selected.size} selected part(s) into a single assembly-style ZBC. All source parts remain untouched.
                    </div>
                    <div>
                        <label className="eyebrow text-slate-500 block mb-1">Combined Part Number *</label>
                        <input
                            type="text"
                            data-testid="combine-part-number"
                            value={combinePartNo}
                            onChange={(e) => setCombinePartNo(e.target.value)}
                            placeholder="e.g. ASSY-001"
                            className="w-full h-9 px-3 border border-slate-200 rounded-md text-[13px]"
                        />
                    </div>
                    <div>
                        <label className="eyebrow text-slate-500 block mb-1">Part Name</label>
                        <input
                            type="text"
                            data-testid="combine-part-name"
                            value={combineName}
                            onChange={(e) => setCombineName(e.target.value)}
                            placeholder="Assembly description"
                            className="w-full h-9 px-3 border border-slate-200 rounded-md text-[13px]"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <SlateButton onClick={() => setCombineOpen(false)} variant="outline">Cancel</SlateButton>
                        <CopperButton
                            onClick={submitCombine}
                            disabled={combineSubmitting || !combinePartNo.trim()}
                            data-testid="combine-submit-btn"
                        >
                            {combineSubmitting ? "Creating…" : `Create Combined ZBC (${selected.size})`}
                        </CopperButton>
                    </div>
                </div>
            </Modal>
            </div>

            {/* Financial Impact modal */}
            <div data-testid="impact-modal" data-open={impactOpen ? "true" : "false"}>
            <Modal
                open={impactOpen}
                onClose={() => setImpactOpen(false)}
                title={`Financial Impact · ${selected.size} ZBC${selected.size === 1 ? "" : "s"} selected`}
                size="3xl"
            >
                {impactLoading && !impactData && <LoadingState />}
                {impactData && (
                    <div className="space-y-4">
                        <div className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
                            <span className="font-semibold">How this is calculated: </span>
                            Per-piece <b>Delta = Supplier Quote − Should Cost</b>. Positive delta means the
                            supplier is charging <span className="text-red-600 font-semibold">above</span> CostIQ&#39;s should-cost
                            (opportunity to negotiate down). Annualised savings = Delta × Annual Volume.
                            Annual Volume defaults to the ZBC&#39;s <code>Qty/BOM</code>, edit inline to model any AAV / EAU.
                        </div>

                        {/* Roll-up cards */}
                        <div className="grid grid-cols-4 gap-3">
                            <ImpactStat
                                label="Total Should-Cost (annualised)"
                                value={fmtINR(impactData.totals.total_annualised_should_cost)}
                                tone="slate"
                            />
                            <ImpactStat
                                label="Total Supplier Cost (annualised)"
                                value={fmtINR(impactData.totals.total_annualised_supplier_cost)}
                                tone="slate"
                            />
                            <ImpactStat
                                label={impactData.totals.total_annualised_savings >= 0 ? "Overpayment (annualised)" : "Under-quoted (annualised)"}
                                value={fmtINR(Math.abs(impactData.totals.total_annualised_savings))}
                                tone={impactData.totals.total_annualised_savings >= 0 ? "red" : "emerald"}
                                icon={impactData.totals.total_annualised_savings >= 0 ? TrendingUp : TrendingDown}
                            />
                            <ImpactStat
                                label="Avg Δ per piece"
                                value={`${impactData.totals.avg_per_piece_delta_pct >= 0 ? "+" : ""}${impactData.totals.avg_per_piece_delta_pct}%`}
                                tone={impactData.totals.avg_per_piece_delta_pct >= 0 ? "red" : "emerald"}
                            />
                        </div>

                        <div className="text-[11px] text-slate-500">
                            {impactData.parts_with_quote} of {impactData.parts_count} selected part(s) have a supplier quote on file: parts without a quote show zero impact.
                        </div>

                        {/* Line-item table */}
                        <div className="border border-slate-200 rounded-md overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-[12px]" data-testid="impact-table">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="eyebrow text-left py-2 px-3">Part Number</th>
                                            <th className="eyebrow text-right py-2 px-3">Should ₹/pc</th>
                                            <th className="eyebrow text-right py-2 px-3">Supplier ₹/pc</th>
                                            <th className="eyebrow text-right py-2 px-3">Δ /pc</th>
                                            <th className="eyebrow text-right py-2 px-3">Annual Volume</th>
                                            <th className="eyebrow text-right py-2 px-3">Annualised Should</th>
                                            <th className="eyebrow text-right py-2 px-3">Annualised Supplier</th>
                                            <th className="eyebrow text-right py-2 px-3">Annualised Impact</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {impactData.lines.map((l) => (
                                            <tr key={l.zbc_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                                                <td className="py-2 px-3">
                                                    <Link
                                                        to={`/zbc/${l.zbc_id}`}
                                                        className="font-mono font-semibold hover:text-[hsl(var(--copper))]"
                                                    >
                                                        {l.part_number}
                                                    </Link>
                                                    <div className="text-[10.5px] text-slate-500 truncate max-w-[180px]">{l.part_name}</div>
                                                </td>
                                                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmtINR(l.should_cost, 2)}</td>
                                                <td className="py-2 px-3 text-right font-mono tabular-nums">
                                                    {l.has_quote ? fmtINR(l.supplier_quote, 2) : <span className="text-slate-400">—</span>}
                                                </td>
                                                <td className={`py-2 px-3 text-right font-mono tabular-nums font-semibold ${
                                                    !l.has_quote ? "text-slate-400" :
                                                    l.per_piece_delta > 0 ? "text-red-600" :
                                                    l.per_piece_delta < 0 ? "text-emerald-600" : "text-slate-700"
                                                }`}>
                                                    {l.has_quote ? (
                                                        <>{l.per_piece_delta >= 0 ? "+" : ""}{fmtINR(l.per_piece_delta, 2)}<div className="text-[10px] opacity-70">({l.per_piece_delta_pct >= 0 ? "+" : ""}{l.per_piece_delta_pct}%)</div></>
                                                    ) : "—"}
                                                </td>
                                                <td className="py-2 px-3 text-right">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        value={impactVolumes[l.zbc_id] ?? l.annual_volume}
                                                        onChange={(e) => updateImpactVolume(l.zbc_id, e.target.value)}
                                                        data-testid={`impact-volume-${l.zbc_id}`}
                                                        className="w-24 h-7 px-2 text-right font-mono text-[12px] border border-slate-200 rounded focus:border-[hsl(var(--copper))] focus:outline-none"
                                                    />
                                                </td>
                                                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmtINR(l.annualised_should_cost)}</td>
                                                <td className="py-2 px-3 text-right font-mono tabular-nums">
                                                    {l.has_quote ? fmtINR(l.annualised_supplier_cost) : <span className="text-slate-400">—</span>}
                                                </td>
                                                <td className={`py-2 px-3 text-right font-mono tabular-nums font-semibold ${
                                                    !l.has_quote ? "text-slate-400" :
                                                    l.annualised_savings > 0 ? "text-red-600" :
                                                    l.annualised_savings < 0 ? "text-emerald-600" : "text-slate-700"
                                                }`}>
                                                    {l.has_quote ? (
                                                        <>{l.annualised_savings >= 0 ? "+" : ""}{fmtINR(l.annualised_savings)}</>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                                        <tr className="font-semibold">
                                            <td className="py-1.5 px-3 text-[12.5px]">TOTAL</td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(impactData.totals.total_should_cost_per_piece, 2)}</td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(impactData.totals.total_supplier_quote_per_piece, 2)}</td>
                                            <td className="py-1.5 px-3"></td>
                                            <td className="py-1.5 px-3"></td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(impactData.totals.total_annualised_should_cost)}</td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums">{fmtINR(impactData.totals.total_annualised_supplier_cost)}</td>
                                            <td className={`py-1.5 px-3 text-right font-mono tabular-nums ${
                                                impactData.totals.total_annualised_savings > 0 ? "text-red-600" :
                                                impactData.totals.total_annualised_savings < 0 ? "text-emerald-600" : "text-slate-700"
                                            }`} data-testid="impact-total-savings">
                                                {impactData.totals.total_annualised_savings >= 0 ? "+" : ""}{fmtINR(impactData.totals.total_annualised_savings)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <SlateButton variant="outline" onClick={() => setImpactOpen(false)}>Close</SlateButton>
                        </div>
                    </div>
                )}
            </Modal>
            </div>
        </div>
    );
}

function ImpactStat({ label, value, tone = "slate", icon: Icon }) {
    const toneMap = {
        slate: "bg-slate-50 border-slate-200 text-slate-800",
        red: "bg-red-50 border-red-200 text-red-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    };
    return (
        <div className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`}>
            <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-70 flex items-center gap-1">
                {Icon && <Icon className="w-3 h-3" />} {label}
            </div>
            <div className="text-[18px] font-mono tabular-nums font-semibold mt-1">{value}</div>
        </div>
    );
}
